import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ProtectedEmail, ProtectedPhone } from '@/components/ProtectedContact';

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-white py-16" data-testid="cookie-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Cookie Policy
          </h1>
          <p className="text-slate-500 mb-8">
            Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}
          </p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                1. Cosa sono i Cookie
              </h2>
              <p className="text-slate-600 mb-4">
                I cookie sono piccoli file di testo che i siti visitati inviano al browser dell'utente, 
                dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva. 
                I cookie sono utilizzati per diverse finalità, hanno caratteristiche diverse e possono 
                essere utilizzati sia dal titolare del sito che si sta visitando, sia da terze parti.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                2. Tipologie di Cookie Utilizzati
              </h2>
              
              <div className="space-y-6">
                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Cookie Tecnici Necessari
                  </h3>
                  <p className="text-slate-600 mb-2">
                    Sono essenziali per il corretto funzionamento del sito. Senza questi cookie, 
                    il sito o alcune sue parti potrebbero non funzionare correttamente.
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 text-sm">
                    <li>Cookie di sessione per l'autenticazione</li>
                    <li>Cookie per le preferenze di consenso</li>
                    <li>Cookie per la sicurezza del sito</li>
                  </ul>
                  <p className="text-sm text-indigo-600 mt-2">
                    <strong>Durata:</strong> Sessione / 12 mesi<br />
                    <strong>Base giuridica:</strong> Legittimo interesse
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Cookie Analitici
                  </h3>
                  <p className="text-slate-600 mb-2">
                    Ci permettono di capire come i visitatori interagiscono con il sito, 
                    raccogliendo informazioni in forma aggregata e anonima.
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 text-sm">
                    <li>Pagine visitate e tempo di permanenza</li>
                    <li>Origine del traffico</li>
                    <li>Dispositivo e browser utilizzato</li>
                  </ul>
                  <p className="text-sm text-indigo-600 mt-2">
                    <strong>Durata:</strong> 24 mesi<br />
                    <strong>Base giuridica:</strong> Consenso
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Cookie Funzionali
                  </h3>
                  <p className="text-slate-600 mb-2">
                    Permettono di ricordare le scelte dell'utente e fornire funzionalità 
                    personalizzate come il chatbot e la chat in tempo reale.
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 text-sm">
                    <li>Preferenze di lingua</li>
                    <li>Sessione chatbot</li>
                    <li>Impostazioni di visualizzazione</li>
                  </ul>
                  <p className="text-sm text-indigo-600 mt-2">
                    <strong>Durata:</strong> 12 mesi<br />
                    <strong>Base giuridica:</strong> Consenso
                  </p>
                </div>

                <div className="bg-slate-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Cookie di Marketing
                  </h3>
                  <p className="text-slate-600 mb-2">
                    Utilizzati per tracciare i visitatori sui siti web con l'intento di 
                    mostrare annunci pertinenti e coinvolgenti per il singolo utente.
                  </p>
                  <ul className="list-disc pl-6 text-slate-600 text-sm">
                    <li>Facebook Pixel (se attivo)</li>
                    <li>Google Ads (se attivo)</li>
                  </ul>
                  <p className="text-sm text-indigo-600 mt-2">
                    <strong>Durata:</strong> Variabile<br />
                    <strong>Base giuridica:</strong> Consenso
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                3. Cookie di Terze Parti
              </h2>
              <p className="text-slate-600 mb-4">
                Il sito potrebbe contenere cookie di terze parti per le seguenti finalità:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-slate-200">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="border border-slate-200 p-3 text-left">Servizio</th>
                      <th className="border border-slate-200 p-3 text-left">Finalità</th>
                      <th className="border border-slate-200 p-3 text-left">Privacy Policy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-200 p-3">Google Fonts</td>
                      <td className="border border-slate-200 p-3">Caricamento font</td>
                      <td className="border border-slate-200 p-3">
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                          Link
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-slate-200 p-3">Facebook</td>
                      <td className="border border-slate-200 p-3">Widget social</td>
                      <td className="border border-slate-200 p-3">
                        <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                          Link
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                4. Gestione dei Cookie
              </h2>
              <p className="text-slate-600 mb-4">
                L'utente può gestire le proprie preferenze sui cookie in diversi modi:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>
                  <strong>Banner di consenso:</strong> Al primo accesso al sito, è possibile 
                  accettare o rifiutare i cookie opzionali tramite il banner.
                </li>
                <li>
                  <strong>Impostazioni del browser:</strong> È possibile bloccare o eliminare 
                  i cookie attraverso le impostazioni del proprio browser.
                </li>
                <li>
                  <strong>Link di opt-out:</strong> Per i cookie di terze parti, è possibile 
                  utilizzare i link di opt-out forniti dai rispettivi provider.
                </li>
              </ul>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-amber-800 text-sm">
                  <strong>Attenzione:</strong> La disabilitazione dei cookie tecnici potrebbe 
                  compromettere il corretto funzionamento del sito.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                5. Come Disabilitare i Cookie nel Browser
              </h2>
              <p className="text-slate-600 mb-4">
                Di seguito i link alle guide per la gestione dei cookie nei principali browser:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>
                  <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                    Safari
                  </a>
                </li>
                <li>
                  <a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                    Microsoft Edge
                  </a>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                6. Aggiornamenti alla Cookie Policy
              </h2>
              <p className="text-slate-600">
                La presente Cookie Policy può essere soggetta a modifiche. Si consiglia di 
                consultare periodicamente questa pagina per essere informati su eventuali 
                aggiornamenti. La data dell'ultimo aggiornamento è indicata in cima alla pagina.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">
                7. Contatti
              </h2>
              <p className="text-slate-600">
                Per qualsiasi domanda relativa alla presente Cookie Policy, è possibile contattare:
              </p>
              <div className="bg-slate-50 p-4 rounded-lg mt-4">
                <p className="text-slate-700">
                  <strong>Studio Professionale Azzano Cantarutti SRL STP</strong><br />
                  Email: <ProtectedEmail user="info" domain="studioazzanostp.it" className="text-indigo-600 hover:underline" /><br />
                  Tel: <ProtectedPhone parts={['+39', '0427', '40326']} display="+39 0427 40326" className="text-indigo-600 hover:underline" /><br />
                  P.IVA: IT03196730307 | C.F.: 03196730307
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
